import React, { useEffect, useMemo, useState } from "react";
import AdminLayout from "../admin/AdminDashboardLayout";
import { useShift, useHeartbeat } from "../../hooks/useShift";
import { useShiftLocations } from "../../hooks/useShiftLocations";
import { ShiftLocation } from "../../hooks/useShift";
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays } from "date-fns";
import PlaceIcon from '@mui/icons-material/Place';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AlarmOffIcon from '@mui/icons-material/AlarmOff';
import AlarmOnIcon from '@mui/icons-material/AlarmOn';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import "react-datepicker/dist/react-datepicker.css";
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useNavigate } from 'react-router-dom';
import toast from "react-hot-toast";
import { useShiftEngagements, calculateEngagementStats } from "../../hooks/useShiftEngagements";
import { useUserAnalytics } from "../../hooks/useUserAnalytics";
import RegistrationIntensity from "../../components/ui/RegistrationIntensity";
import MapTracking from "../../components/ui/MapTracking";
import { useApiMutation } from "../../hooks/useMutation";
import DraftBatchSubmissionModal from "../../components/canvasser/DraftBatchSubmissionModal";

type PaperFormBatchDraft = {
  id: number;
  partnerId: number;
  canvasserId: number;
  claimedForms: number;
  verifiedForms: number;
  rejectedForms: number;
  incompleteForms: number;
  status: string;
  verificationMode: string | null;
  verifiedByUserId: number | null;
  verifiedAt: string | null;
  submittedAt: string | null;
  overdueFlag: boolean;
  hoursToVerify: number | null;
  accuracyScore: number | null;
  rejectionRate: number | null;
  timelinessScore: number | null;
  finalQualityScore: number | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
};

type CurrentDraftBatchResponse = {
  success: boolean;
  message: string;
  data: PaperFormBatchDraft | null;
};

const getApiErrorMessage = (error: any) => {
  return error?.response?.data?.message || error?.message || "";
};

const isNoDraftBatchError = (error: any) => {
  const message = getApiErrorMessage(error).toLowerCase();
  return (
    error?.response?.status === 404 ||
    message.includes("no draft") ||
    message.includes("draft not found") ||
    message.includes("no current paper form draft")
  );
};

// Custom styles for Leaflet markers
const leafletStyles = `
  .custom-marker {
    background: transparent !important;
    border: none !important;
  }
  .leaflet-popup-content-wrapper {
    border-radius: 8px;
  }
  .leaflet-popup-content {
    margin: 8px 12px;
  }
`;

// Inject styles into the document
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = leafletStyles;
  document.head.appendChild(styleSheet);
}

interface LocationInfo {
  lat: number | null;
  lng: number | null;
  placeName: string;
}

const SAMPLE_SHIFT_HISTORY: Record<string, number> = {
  // ISO date: hours worked
  "2025-01-02": 2.5,
  "2025-01-05": 6,
  "2025-01-07": 3,
  "2025-01-12": 7.5,
  "2025-01-16": 5,
  "2025-01-19": 1.5,
};



// Fix for default markers in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const SAMPLE_TRACK = [
  { lat: 6.5244, lng: 3.3792, label: "Start - Lagos" },
  { lat: 7.3775, lng: 3.9470, label: "En route - Abeokuta" },
  { lat: 9.0765, lng: 7.3986, label: "End - Abuja" },
];




export default function CanvasserShifts() {
  const navigate = useNavigate();
  const [now, setNow] = useState<Date>(new Date());
  const [showModal, setShowModal] = useState(false);
  const [showDraftSubmissionModal, setShowDraftSubmissionModal] = useState(false);
  const [location, setLocation] = useState<LocationInfo>({ lat: null, lng: null, placeName: "Locating..." });
  const [locError, setLocError] = useState<string | null>(null);
  const [draftBatch, setDraftBatch] = useState<PaperFormBatchDraft | null>(null);

  // Use shift hook
  const {
    currentShift,
    isLoading,
    error,
    refetch,
    startShift,
    pauseShift,
    endShift,
    resumeShift,
    sendHeartbeat,
    isStartingShift,
    isPausingShift,
    isEndingShift,
    isResumingShift,
    isSendingHeartbeat,
  } = useShift();

  const currentDraftBatchMutation = useApiMutation<CurrentDraftBatchResponse>({
    onError: (error: any) => {
      if (isNoDraftBatchError(error)) return;

      const errorMessage = getApiErrorMessage(error) || 'Unable to check current paper form drafts.';
      toast.error(errorMessage);
    },
  });

  const submitDraftBatchMutation = useApiMutation({
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || error?.message || 'Unable to submit your draft batch.';
      toast.error(errorMessage);
    },
  });

  // Fetch shift engagements and locations data
  const { data: engagementsData, isLoading: engagementsLoading } = useShiftEngagements(currentShift?.id);
  const { data: locationsData, isLoading: locationsLoading } = useShiftLocations(currentShift?.id);
  
  // Fetch user analytics data
  const { data: userAnalyticsData, isLoading: analyticsLoading } = useUserAnalytics();
  
  // Calculate statistics from engagement data
  const engagementStats = useMemo(() => {
    if (!engagementsData?.data) return {
      totalRegistrations: 0,
      todayRegistrations: 0,
      registeredCount: 0,
      unregisteredCount: 0,
      reminderCount: 0,
      registrationRate: 0,
    };
    
    return calculateEngagementStats(engagementsData.data);
  }, [engagementsData]);

  // Extract user analytics data
  const userAnalytics = useMemo(() => {
    if (!userAnalyticsData?.data?.analytics) {
      return {
        totalLeads: 0,
        registeredLeads: 0,
        unregisteredLeads: 0,
        remindersSet: 0,
      };
    }
    return userAnalyticsData.data.analytics;
  }, [userAnalyticsData]);

  // Use heartbeat functionality
  const { sendManualHeartbeat } = useHeartbeat(currentShift, sendHeartbeat);

  const hasCurrentShift = !!currentShift;
  const isActive = currentShift?.status === 'ACTIVE';
  const isPaused = currentShift?.status === 'PAUSED';
  const startTime = currentShift ? new Date(currentShift.startedAt) : null;

  useEffect(() => {
    if (isActive) {
      const id = setInterval(() => setNow(new Date()), 60_000);
      return () => clearInterval(id);
    }
  }, [isActive]);

  // Refetch shifts when component mounts
  useEffect(() => {
    refetch();
  }, [refetch]);

  const hoursToday = useMemo(() => {
    if (!startTime) return 0;
    const diffMs = now.getTime() - startTime.getTime();
    return Math.max(diffMs / (1000 * 60 * 60), 0);
  }, [now, startTime]);

  const shiftTimeAnalytics = useMemo(() => {
    const targetHours = 12;
    const totalMinutes = Math.floor(hoursToday * 60);
    const elapsedHours = Math.floor(totalMinutes / 60);
    const elapsedMinutes = totalMinutes % 60;
    const remainingMinutes = Math.max(Math.round((targetHours - hoursToday) * 60), 0);
    const remainingHoursDisplay = Math.floor(remainingMinutes / 60);
    const remainingMinutesDisplay = remainingMinutes % 60;
    const progressPercent = Math.min(Math.round((hoursToday / targetHours) * 100), 100);

    return {
      targetHours,
      elapsedHours,
      elapsedMinutes,
      remainingHoursDisplay,
      remainingMinutesDisplay,
      progressPercent,
    };
  }, [hoursToday]);


  const fetchLocation = (): Promise<LocationInfo | null> => {
    setLocError(null);
    if (!navigator.geolocation) {
      setLocError("Geolocation not supported");
      return Promise.resolve(null);
    }
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const lat = pos.coords.latitude;
          const lng = pos.coords.longitude;
          const currentLocation = { lat, lng, placeName: "Location found" };
          setLocation(currentLocation);
          resolve(currentLocation);

          try {
            const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`);
            const json = await res.json();
            const placeName = json?.display_name || "";
            if (placeName) {
              setLocation({ lat, lng, placeName });
            }
          } catch (err) {
            // Coordinates are enough to start a shift; the address label is optional.
          }
        },
        (err) => {
          setLocError(err.message);
          setLocation({ lat: null, lng: null, placeName: "" });
          resolve(null);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000,
        }
      );
    });
  };

  useEffect(() => {
    if (showModal) {
      void fetchLocation();
    }
  }, [showModal]);

  // Fetch location in background when component mounts and periodically
  useEffect(() => {
    // Initial location fetch
    void fetchLocation();
    
    // Set up periodic location updates every 30 seconds
    const locationInterval = setInterval(() => {
      void fetchLocation();
    }, 30000);
    
    return () => clearInterval(locationInterval);
  }, []);

  const handleStart = async () => {
    const currentLocation = location.lat == null || location.lng == null
      ? await fetchLocation()
      : location;

    if (currentLocation?.lat == null || currentLocation.lng == null) {
      setLocError("Location required to start shift");
      return;
    }

    try {
      const shiftLocation: ShiftLocation = {
        latitude: currentLocation.lat,
        longitude: currentLocation.lng,
        accuracyMeters: 10,
        locationSource: "GPS",
        locationLabel: currentLocation.placeName || "Unknown Location",
      };
      
      await startShift(shiftLocation);
      setShowModal(false);
    } catch (error) {
      console.error("Failed to start shift:", error);
    }
  };

  const handlePause = async () => {
    if (!currentShift || !location.lat || !location.lng) {
      setLocError("Location required to pause shift");
      return;
    }

    try {
      const shiftLocation: ShiftLocation = {
        latitude: location.lat,
        longitude: location.lng,
        accuracyMeters: 8,
        locationSource: "GPS",
        locationLabel: location.placeName || "Unknown Location",
      };
      
      await pauseShift(currentShift.id, shiftLocation);
    } catch (error) {
      console.error("Failed to pause shift:", error);
    }
  };

  const handleResume = async () => {
    if (!currentShift) {
      setLocError("No current shift found");
      return;
    }
    
    const currentLocation = location.lat == null || location.lng == null
      ? await fetchLocation()
      : location;

    if (currentLocation?.lat == null || currentLocation.lng == null) {
      setLocError("Location required to resume shift");
      toast.error("Location is needed to resume shift. Please enable location permissions.");
      return;
    }

    try {
      const shiftLocation: ShiftLocation = {
        latitude: currentLocation.lat,
        longitude: currentLocation.lng,
        accuracyMeters: 8,
        locationSource: "GPS",
        locationLabel: currentLocation.placeName || "Unknown Location",
      };
      
      await resumeShift(currentShift.id, shiftLocation);
    } catch (error) {
      console.error("Failed to resume shift:", error);
    }
  };

  const completeEndShift = async () => {
    if (!currentShift || !location.lat || !location.lng) {
      setLocError("Location required to end shift");
      return;
    }

    try {
      const shiftLocation: ShiftLocation = {
        latitude: location.lat,
        longitude: location.lng,
        accuracyMeters: 12,
        locationSource: "GPS",
        locationLabel: location.placeName || "Unknown Location",
      };
      
      await endShift(currentShift.id, shiftLocation);
    } catch (error) {
      console.error("Failed to end shift:", error);
    }
  };

  const handleSubmitDraftAndEndShift = async (supervisorId: number | null) => {
    if (!draftBatch) return;

    try {
      

      await toast.promise(
        submitDraftBatchMutation.mutateAsync({
          method: "POST",
          url: `/paper-form-batches/${draftBatch.id}/submit-draft`,
          data: supervisorId !== null ? { supervisorId } : undefined,
        }),
        {
          loading: "Submitting draft batch to supervisor...",
          success: "Draft submitted successfully.",
          error: (error: any) => error?.message || "Unable to submit your draft batch.",
        }
      );

      setShowDraftSubmissionModal(false);
      setDraftBatch(null);
      await completeEndShift();
    } catch {
      // Toast handled above.
    }
  };

  const handleEnd = async () => {
    if (!currentShift) return;

    try {
      const response = await currentDraftBatchMutation.mutateAsync({
        method: "GET",
        url: "/paper-form-batches/current-draft",
      });

      if (response?.data) {
        setDraftBatch(response.data);
        setShowDraftSubmissionModal(true);
        return;
      }

      toast.success("No drafts found. Proceeding to end shift.");
      await completeEndShift();
    } catch (error) {
      if (isNoDraftBatchError(error)) {
        toast.success("No drafts found. Proceeding to end shift.");
        await completeEndShift();
      }
    }
  };

  const getShiftStatus = () => {
    if (!hasCurrentShift) return { label: "Inactive", color: "bg-gray-100 text-gray-700" };
    if (isPaused) return { label: "Paused", color: "bg-yellow-100 text-yellow-700" };
    if (isActive) return { label: "Active", color: "bg-green-100 text-green-700" };
    return { label: "Inactive", color: "bg-gray-100 text-gray-700" };
  };

  const shiftStatus = getShiftStatus();

  // Calendar grid
  const month = new Date();
  const monthStart = startOfMonth(month);
  const monthEnd = endOfMonth(month);
  const startDate = startOfWeek(monthStart, { weekStartsOn: 0 });
  const endDate = endOfWeek(monthEnd, { weekStartsOn: 0 });

  const days: Date[] = [];
  let day = startDate;
  while (day <= endDate) {
    days.push(day);
    day = addDays(day, 1);
  }

  return (
    <AdminLayout role="CANVASSER">
      <div className="space-y-6 bg-slate-50/70 text-slate-950">
        <div className="overflow-hidden rounded-3xl border border-red-100 bg-white shadow-sm">
          <div className="flex flex-col gap-5 p-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-red-50 text-red-600">
                <AccessTimeIcon />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-red-600">Canvasser Operations</p>
                <h2 className="mt-1 text-3xl font-semibold tracking-tight text-slate-950">Shift Logging</h2>
                <p className="mt-2 max-w-2xl text-sm text-slate-500">Track shift time, field activity, registrations, and movement from one focused workspace.</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <span className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold ${shiftStatus.color}`}>
                <span className={`h-2.5 w-2.5 rounded-full ${isActive ? "bg-green-500" : isPaused ? "bg-yellow-500" : "bg-slate-400"}`} />
                {shiftStatus.label}
              </span>
              <span className="rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-medium text-slate-600">
                {format(now, "PP")}
              </span>
            </div>
          </div>
        </div>

        {/* Analytics row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          <div className="group rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-red-200 hover:shadow-md">
            <div className="mb-4 flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Total Engagements</p>
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-red-50 text-red-600">
                <PeopleAltIcon fontSize="small" />
              </span>
            </div>
            <p className="text-3xl font-semibold tracking-tight text-slate-950">
              {analyticsLoading ? '...' : userAnalytics.totalLeads.toLocaleString()}
            </p>
            <p className="mt-2 text-sm text-slate-500">All-time leads contacted</p>
          </div>
          <div className="group rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-red-200 hover:shadow-md">
            <div className="mb-4 flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Total Registrations</p>
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-red-50 text-red-600">
                <HowToRegIcon fontSize="small" />
              </span>
            </div>
            <p className="text-3xl font-semibold tracking-tight text-slate-950">
              {analyticsLoading ? '...' : userAnalytics.registeredLeads.toLocaleString()}
            </p>
            <p className="mt-2 text-sm text-slate-500">Successfully registered voters</p>
          </div>
          <div className="group rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-red-200 hover:shadow-md">
            <div className="mb-4 flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Unregistered</p>
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-red-50 text-red-600">
                <AlarmOffIcon fontSize="small" />
              </span>
            </div>
            <p className="text-3xl font-semibold tracking-tight text-slate-950">
              {analyticsLoading ? '...' : userAnalytics.unregisteredLeads.toLocaleString()}
            </p>
            <p className="mt-2 text-sm text-slate-500">Leads not yet registered</p>
          </div>
          <div className="group rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-red-200 hover:shadow-md">
            <div className="mb-4 flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Reminders Set</p>
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-red-50 text-red-600">
                <AlarmOnIcon fontSize="small" />
              </span>
            </div>
            <p className="text-3xl font-semibold tracking-tight text-slate-950">
              {analyticsLoading ? '...' : userAnalytics.remindersSet.toLocaleString()}
            </p>
            <p className="mt-2 text-sm text-slate-500">Voters with reminders</p>
          </div>
          <div className={`group rounded-2xl border bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${!isActive ? 'border-slate-200 opacity-60' : 'border-red-200'}`}>
            <div className="mb-4 flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Current Shift</p>
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-red-600 text-white">
                <TrendingUpIcon fontSize="small" />
              </span>
            </div>
            <p className={`text-3xl font-semibold tracking-tight ${!isActive ? 'text-slate-400' : 'text-red-600'}`}>
              {!isActive ? '0' : (engagementsLoading ? '...' : engagementStats.totalRegistrations.toLocaleString())}
            </p>
            <p className="mt-2 text-sm text-slate-500">
              {!isActive ? 'No active shift' : 'Current shift registrations'}
            </p>
          </div>
        </div>

        {/* Shift controls row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            {/* <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-cyan-400 via-blue-500 to-indigo-500" /> */}
            <div className="flex flex-col gap-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Shift Time</p>
                  <div className="mt-3 flex items-end gap-2">
                    <span className="text-5xl font-semibold leading-none text-slate-950">
                      {shiftTimeAnalytics.elapsedHours}
                    </span>
                    <span className="pb-1 text-sm font-medium text-slate-500">h</span>
                    <span className="text-5xl font-semibold leading-none text-slate-950">
                      {String(shiftTimeAnalytics.elapsedMinutes).padStart(2, "0")}
                    </span>
                    <span className="pb-1 text-sm font-medium text-slate-500">m</span>
                  </div>
                </div>
                <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold ${shiftStatus.color}`}>
                  <span className={`h-2 w-2 rounded-full ${isActive ? "bg-green-500" : isPaused ? "bg-yellow-500" : "bg-slate-400"}`} />
                  {shiftStatus.label}
                </span>
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between text-xs font-medium text-slate-500">
                  <span>Daily target progress</span>
                  <span>{shiftTimeAnalytics.progressPercent}%</span>
                </div>
                <div className="h-3 overflow-hidden rounded-full bg-slate-100">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-lime-400 via-lime-500 to-green-500 transition-all duration-500"
                    style={{ width: `${shiftTimeAnalytics.progressPercent}%` }}
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Target</p>
                  <p className="mt-1 text-lg font-semibold text-slate-900">{shiftTimeAnalytics.targetHours}h</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Remaining</p>
                  <p className="mt-1 text-lg font-semibold text-slate-900">
                    {shiftTimeAnalytics.remainingHoursDisplay}h {String(shiftTimeAnalytics.remainingMinutesDisplay).padStart(2, "0")}m
                  </p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Started</p>
                  <p className="mt-1 text-lg font-semibold text-slate-900">
                    {startTime ? format(startTime, "p") : "--"}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex h-full flex-col justify-between gap-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-orange-600">Shift Control</p>
                  <h3 className="mt-2 text-xl font-semibold text-slate-950">Manage field availability</h3>
                  <p className="mt-1 text-sm text-slate-500">Start, pause, resume, or end your current shift.</p>
                </div>
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-orange-50 text-orange-600">
                  <PlayArrowIcon />
                </span>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex items-start gap-3">
                  <PlaceIcon className="mt-0.5 text-orange-600" fontSize="small" />
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Current location</p>
                    <p className="mt-1 break-words text-xs text-slate-500">{location.placeName || locError || "Fetching location..."}</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                {!hasCurrentShift ? (
                  <button
                    onClick={() => setShowModal(true)}
                    className="inline-flex items-center gap-2 rounded-xl bg-orange-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-orange-700 disabled:opacity-60"
                  >
                    <PlayArrowIcon fontSize="small" />
                    Start shift
                  </button>
                ) : (
                  <>
                    {!isPaused ? (
                      <button
                        onClick={handlePause}
                        disabled={isPausingShift}
                        className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:border-orange-200 hover:bg-orange-50 disabled:opacity-60"
                      >
                        Pause shift
                      </button>
                    ) : (
                      <button
                        onClick={handleResume}
                        disabled={isResumingShift}
                        className="inline-flex items-center gap-2 rounded-xl bg-orange-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-orange-700 disabled:opacity-60"
                      >
                        <PlayArrowIcon fontSize="small" />
                        Resume shift
                      </button>
                    )}
                    <button
                      onClick={handleEnd}
                      disabled={currentDraftBatchMutation.isPending || submitDraftBatchMutation.isPending || isEndingShift}
                      className="inline-flex items-center gap-2 rounded-xl bg-red-800 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-red-700 disabled:opacity-60"
                    >
                      <StopIcon fontSize="small" />
                      {currentDraftBatchMutation.isPending ? "Checking drafts..." : "End shift"}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Calendar + Map */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-orange-600">Activity Pattern</p>
                <h3 className="mt-1 text-lg font-semibold text-slate-950">Registration intensity</h3>
              </div>
              <span className="rounded-full bg-orange-50 px-3 py-1 text-xs font-semibold text-orange-600">Monthly</span>
            </div>
            <RegistrationIntensity />
          </div>
          
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-orange-600">Field Movement</p>
                <h3 className="mt-1 text-lg font-semibold text-slate-950">Live route tracking</h3>
              </div>
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                {isActive ? "Live" : "Idle"}
              </span>
            </div>
            <MapTracking shiftId={currentShift?.id} />
          </div>
        </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 z-[999999] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-slate-950/45 backdrop-blur-sm" onClick={() => setShowModal(false)} />
            <div className="relative z-10 w-[560px] max-w-[95%] overflow-hidden rounded-3xl border border-white/80 bg-white text-slate-950 shadow-2xl">
              <div className="flex items-center justify-between border-b border-slate-100 px-6 py-5">
                <div className="flex items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-orange-50 text-orange-600">
                    <PlayArrowIcon />
                  </span>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-orange-600">Start Shift</p>
                    <h3 className="text-xl font-semibold text-slate-950">Confirm field check-in</h3>
                  </div>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-500 transition hover:bg-orange-50 hover:text-orange-600"
                >
                  ×
                </button>
              </div>

              <div className="space-y-4 p-6">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-center gap-3 text-slate-700">
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-orange-600 shadow-sm">
                      <AccessTimeIcon fontSize="small" />
                    </span>
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Current time</p>
                      <p className="mt-1 text-sm font-semibold text-slate-950">{format(new Date(), "PPpp")}</p>
                    </div>
                  </div>
                </div>
                {/* <div className="flex items-center gap-2 text-gray-700">
                  <RoomIcon fontSize="small" />
                  <span>Lat / Lng:</span>
                  <span className="font-semibold">{location.lat?.toFixed(5) || "--"}, {location.lng?.toFixed(5) || "--"}</span>
                </div> */}
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-start gap-3 text-slate-700">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-orange-600 shadow-sm">
                      <PlaceIcon fontSize="small" />
                    </span>
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Current location</p>
                      <p className="mt-1 text-sm font-semibold text-slate-950">{location.placeName || locError || "Fetching location..."}</p>
                    </div>
                  </div>
                </div>
                {locError && <p className="text-xs text-orange-600">{locError}</p>}

                <div className="flex justify-end gap-3 pt-2">
                  <button
                    onClick={() => setShowModal(false)}
                    className="rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                  <button
                  onClick={handleStart}
                  disabled={isStartingShift}
                    className="inline-flex items-center gap-2 rounded-xl bg-orange-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-orange-700 disabled:opacity-60"
                  >
                    <PlayArrowIcon fontSize="small" />
                    {isStartingShift ? "Starting..." : "Start shift"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {showDraftSubmissionModal && draftBatch && (
          <DraftBatchSubmissionModal
            draftBatch={draftBatch}
            isSubmitting={submitDraftBatchMutation.isPending}
            title="Submit your draft batch before ending shift"
            description="There are paper-form registrations in your current draft batch. To end your shift, submit the draft to your supervisor first."
            helperText="Drafts are submitted once a day at the end of your daily shift. If your day is not finished, pause your shift instead."
            submitLabel="Submit draft"
            onClose={() => {
              setShowDraftSubmissionModal(false);
              setDraftBatch(null);
            }}
            onSubmit={handleSubmitDraftAndEndShift}
          />
        )}
      </div>
    </AdminLayout>
  );
}
